import type { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../styles/colors';
import { documentHyphenation, fontFamily, text, type TextRoleKey } from '../../../../styles/onboardingUiTokens';

export type BodyTone = 'default' | 'muted' | 'faded';
export type BodySize = 'default' | 'small' | 'mini';

const bodySizeToRole: Record<BodySize, Extract<TextRoleKey, 'body' | 'small' | 'mini'>> = {
  default: 'body',
  small: 'small',
  mini: 'mini',
};

const StyledBody = styled.p<{ $muted: boolean; $faded: boolean; $size: BodySize; $kit: boolean }>`
  margin: 0;
  font-style: normal;
  letter-spacing: 0;
  ${documentHyphenation}
  ${({ $kit, $size }) => {
    const role = bodySizeToRole[$size];
    const t = text[role];
    const base = `
      font-size: ${t.fontSize};
      line-height: ${t.lineHeight};
      font-weight: ${t.fontWeight};
    `;
    if ($kit) {
      return base;
    }
    return `
      font-family: ${fontFamily.ubuntu};
      ${base}
    `;
  }}
  color: ${({ $muted, $faded }) => {
    if ($faded) {
      return colors.dark;
    }
    return $muted ? colors.medium : colors.dark;
  }};
  opacity: ${({ $faded }) => ($faded ? 0.6 : 1)};
`;

export type BodyProps = HTMLAttributes<HTMLParagraphElement> & {
  children: ReactNode;
  /** Muted uses `colors.medium`. Faded matches legacy `Body60` (60% opacity on dark). */
  tone?: BodyTone;
  /** `default` = legacy Body. `small` / `mini` match legacy Small / Mini. Ignored when `variant="ui"`. */
  size?: BodySize;
  /**
   * `ui` = kit body scale (`text.body` in rem, inherits font family).
   * `document` = Ubuntu + same `text` roles for body / small / mini.
   */
  variant?: 'ui' | 'document';
};

export function Body({ tone = 'default', size = 'default', variant = 'ui', children, ...rest }: BodyProps) {
  const muted = tone === 'muted';
  const faded = tone === 'faded';
  const kit = variant === 'ui';
  return (
    <StyledBody $muted={muted} $faded={faded} $size={size} $kit={kit} {...rest}>
      {children}
    </StyledBody>
  );
}
